import { useEffect, useState } from 'react'
import type { FunilDto, TagDto, MembroDaEquipeDto } from '@crm/shared'
import { juntar } from '../../componentes/ui'
import { IconeBuscar, IconeFiltro, IconeX } from '../../componentes/icones'
import { plural } from '../../formato'

/**
 * Filtros da lista de conversas.
 *
 * Ficam escondidos atrás de um botão porque a coluna é estreita e o uso do
 * dia a dia é sem filtro nenhum. Abertos, ocupam a largura toda — melhor do
 * que espremer cinco seletores numa linha onde nenhum caberia legível.
 *
 * A contagem no botão existe para o filtro nunca ficar ligado sem ninguém
 * perceber: lista vazia com filtro esquecido parece sistema quebrado.
 *
 * O painel é dividido em duas partes, e a divisão é a pergunta que cada uma
 * responde. Em cima, "achar uma conversa": protocolo e palavra dita são
 * buscas que, sozinhas, já devolvem a conversa certa. Embaixo, "estreitar a
 * lista": responsável, etiqueta, funil e período recortam o que está à vista.
 * Empilhados sem separação, os oito campos tinham o mesmo peso e era preciso
 * ler todos para descobrir qual servia.
 */

export interface ValoresDoFiltro {
  /** Protocolo exato ou o começo dele. */
  protocolo: string
  /** Palavra dita em alguma mensagem da conversa. */
  mensagem: string
  responsavelId: string
  tagId: string
  funilId: string
  etapaId: string
  de: string
  ate: string
}

export const FILTRO_VAZIO: ValoresDoFiltro = {
  protocolo: '',
  mensagem: '',
  responsavelId: '',
  tagId: '',
  funilId: '',
  etapaId: '',
  de: '',
  ate: '',
}

export function contarFiltros(v: ValoresDoFiltro): number {
  return Object.values(v).filter(Boolean).length
}

/**
 * O botão que abre os filtros, para caber na mesma linha do recorte.
 *
 * Fica separado do painel porque a coluna de conversas é estreita e cada
 * linha de cabeçalho é altura tirada da lista — o gatilho divide a linha com
 * "todas/minhas/fila", e só o painel abre embaixo.
 */
export function GatilhoDeFiltros({
  valores,
  aberto,
  aoAlternar,
  aoLimpar,
}: {
  valores: ValoresDoFiltro
  aberto: boolean
  aoAlternar: () => void
  aoLimpar: () => void
}) {
  const quantos = contarFiltros(valores)

  return (
    <span className="flex shrink-0 items-center">
      <button
        type="button"
        onClick={aoAlternar}
        title={
          quantos > 0 ? plural(quantos, 'filtro aplicado', 'filtros aplicados') : 'Filtrar a lista'
        }
        aria-label="Filtros"
        className={juntar(
          'relative flex h-7 w-7 items-center justify-center rounded-md transition',
          aberto || quantos > 0
            ? 'bg-marca/10 text-marca'
            : 'text-tinta-400 hover:bg-tinta-100 hover:text-tinta-900',
        )}
      >
        <IconeFiltro className="h-4 w-4" />
        {quantos > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-marca px-0.5 text-[9px] text-white">
            {quantos}
          </span>
        ) : null}
      </button>

      {quantos > 0 ? (
        <button
          type="button"
          onClick={aoLimpar}
          title="Limpar filtros"
          aria-label="Limpar filtros"
          className="flex h-7 w-6 items-center justify-center rounded-md text-tinta-400 transition hover:bg-tinta-100 hover:text-red-600"
        >
          <IconeX className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </span>
  )
}

/**
 * Linha de "rótulo à esquerda, controle à direita".
 *
 * Os rótulos estavam acima de cada campo, e a coluna é estreita: oito campos
 * viravam dezesseis linhas de altura, comendo a lista que o filtro serve para
 * organizar. De lado, o rótulo usa espaço que já estava vazio, e os controles
 * alinham numa coluna só — dá para correr o olho por ela sem ler os nomes.
 */
function Linha({
  rotulo,
  htmlFor,
  children,
}: {
  rotulo: string
  htmlFor?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={htmlFor} className="w-[5.5rem] shrink-0 text-xs font-medium text-tinta-500">
        {rotulo}
      </label>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}

export function FiltrosDaLista({
  valores,
  aoMudar,
  aoLimpar,
  usuarios,
  tags,
  funis,
  status,
  aberto,
}: {
  valores: ValoresDoFiltro
  aoMudar: (v: ValoresDoFiltro) => void
  aoLimpar: () => void
  usuarios: MembroDaEquipeDto[]
  tags: TagDto[]
  funis: FunilDto[]
  status: 'aberto' | 'fechado' | 'todas'
  aberto: boolean
}) {
  const trocar = (campo: keyof ValoresDoFiltro, valor: string) => {
    // Trocar de funil descarta a etapa: etapa de outro funil não filtra nada.
    if (campo === 'funilId') aoMudar({ ...valores, funilId: valor, etapaId: '' })
    else aoMudar({ ...valores, [campo]: valor })
  }

  /*
    A palavra fica em estado próprio e só sobe depois de uma pausa na digitação.

    Cada letra vira uma consulta que varre as mensagens; sem a pausa, escrever
    "gengiva" mandaria sete buscas e as seis primeiras seriam jogadas fora.
  */
  const [palavra, setPalavra] = useState(valores.mensagem)
  useEffect(() => setPalavra(valores.mensagem), [valores.mensagem])
  useEffect(() => {
    if (palavra === valores.mensagem) return
    const relogio = setTimeout(() => trocar('mensagem', palavra), 350)
    return () => clearTimeout(relogio)
  }, [palavra])

  const etapas = funis.find((f) => f.id === valores.funilId)?.etapas ?? []
  const quantos = contarFiltros(valores)

  /*
    Um campo preenchido fica com a borda da marca.

    Com o painel fechado a contagem no ícone avisa que há filtro; aberto, ela
    não diz qual. Sem marca no próprio campo, achar o que está ligado é
    percorrer os oito à procura do que não está em "Qualquer um".
  */
  const campo = (preenchido: boolean) =>
    juntar(
      'w-full rounded-md border bg-superficie px-2.5 py-1.5 text-sm text-tinta-800 outline-none transition',
      'focus:border-marca focus:ring-2 focus:ring-marca/15',
      preenchido ? 'border-marca/60' : 'border-tinta-200 hover:border-tinta-300',
    )

  if (!aberto) return null

  return (
    <div className="border-b border-tinta-200 bg-tinta-50">
      <div className="space-y-3 px-3 py-3">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-tinta-400">
            Achar uma conversa
          </span>
          {quantos > 0 ? (
            <button
              type="button"
              onClick={aoLimpar}
              className="text-xs font-medium text-marca transition hover:underline"
            >
              Limpar {quantos}
            </button>
          ) : null}
        </div>

        {/*
          Buscar em mensagens vem primeiro e é o campo maior do painel.

          Responde a pergunta que o nome não responde: "com quem foi mesmo que
          falei de gengiva?". É o filtro mais usado dos oito, e estava em
          segundo, do mesmo tamanho dos outros.
        */}
        <div>
          <div className="relative">
            <IconeBuscar className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-tinta-400" />
            <input
              value={palavra}
              onChange={(e) => setPalavra(e.target.value)}
              placeholder="Palavra dita na conversa"
              className={juntar(campo(Boolean(valores.mensagem)), 'pl-8')}
            />
          </div>
          <p className="mt-1 text-[11px] text-tinta-400">
            Palavra inteira, como foi escrita. A partir de 3 letras.
          </p>
        </div>

        {/*
          Protocolo é o número que o cliente cita ao ligar. Entra com o "nº"
          colado à esquerda: sem ele, uma caixa vazia de dígitos ao lado de uma
          busca por palavra não diz o que espera receber.
        */}
        <div className="relative">
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-tinta-400">
            nº
          </span>
          <input
            value={valores.protocolo}
            onChange={(e) => trocar('protocolo', e.target.value)}
            placeholder="Protocolo"
            inputMode="numeric"
            className={juntar(campo(Boolean(valores.protocolo)), 'pl-8 font-mono')}
          />
        </div>

        <div className="border-t border-tinta-200 pt-3">
          <span className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-tinta-400">
            Estreitar a lista
          </span>

          <div className="space-y-2">
            <Linha rotulo="Responsável" htmlFor="filtro-responsavel">
              <select
                id="filtro-responsavel"
                value={valores.responsavelId}
                onChange={(e) => trocar('responsavelId', e.target.value)}
                className={campo(Boolean(valores.responsavelId))}
              >
                <option value="">Qualquer um</option>
                {usuarios
                  .filter((u) => u.ativo)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome}
                    </option>
                  ))}
              </select>
            </Linha>

            <Linha rotulo="Etiqueta" htmlFor="filtro-etiqueta">
              <select
                id="filtro-etiqueta"
                value={valores.tagId}
                onChange={(e) => trocar('tagId', e.target.value)}
                className={campo(Boolean(valores.tagId))}
              >
                <option value="">Qualquer uma</option>
                {tags.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nome}
                  </option>
                ))}
              </select>
            </Linha>

            <Linha rotulo="Funil" htmlFor="filtro-funil">
              <select
                id="filtro-funil"
                value={valores.funilId}
                onChange={(e) => trocar('funilId', e.target.value)}
                className={campo(Boolean(valores.funilId))}
              >
                <option value="">Qualquer um</option>
                {funis.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                  </option>
                ))}
              </select>
            </Linha>

            {/*
              A etapa só existe depois do funil, e some enquanto não existe.

              Desabilitada, ela ficava ali cinza-claro ocupando linha e
              parecendo defeito. Aparecendo junto com o funil escolhido, a
              relação entre as duas fica clara sem precisar ser explicada.
            */}
            {valores.funilId ? (
              <Linha rotulo="Etapa" htmlFor="filtro-etapa">
                <select
                  id="filtro-etapa"
                  value={valores.etapaId}
                  onChange={(e) => trocar('etapaId', e.target.value)}
                  className={campo(Boolean(valores.etapaId))}
                >
                  <option value="">Qualquer uma</option>
                  {etapas.map((et) => (
                    <option key={et.id} value={et.id}>
                      {et.nome}
                    </option>
                  ))}
                </select>
              </Linha>
            ) : null}

            {/*
              As duas datas em linhas separadas, e não lado a lado.

              O campo de data do navegador tem largura mínima própria — ele
              mostra "dd/mm/aaaa" e um ícone de calendário. Dois deles com um
              "até" no meio não cabem numa coluna de conversas, e o segundo
              saía cortado pela borda. Cada um na sua linha entra na mesma
              grade de todo o resto do painel.
            */}
            <Linha rotulo={status === 'fechado' ? 'Encerrado de' : 'Aberto de'} htmlFor="filtro-de">
              <input
                id="filtro-de"
                type="date"
                value={valores.de}
                onChange={(e) => trocar('de', e.target.value)}
                className={campo(Boolean(valores.de))}
              />
            </Linha>

            <Linha rotulo="até" htmlFor="filtro-ate">
              <input
                id="filtro-ate"
                type="date"
                value={valores.ate}
                onChange={(e) => trocar('ate', e.target.value)}
                className={campo(Boolean(valores.ate))}
              />
            </Linha>
          </div>
        </div>
      </div>
    </div>
  )
}
